import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http, money, formatErr } from "@/lib/api";
import { ArrowLeft, Package } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["unfulfilled", "processing", "shipped", "delivered", "cancelled"];

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);

  const load = () => http.get(`/admin/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { load(); }, [id]);

  const setStatus = async (val) => {
    try {
      await http.put(`/admin/orders/${id}/status`, { fulfillment_status: val });
      toast.success(`Marked ${val}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const markPaid = async () => {
    try {
      await http.put(`/admin/orders/${id}/status`, { payment_status: "paid" });
      toast.success("Marked paid");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!o) return <div className="text-white/55">Loading…</div>;

  return (
    <div>
      <Link to="/admin/orders" className="text-sm text-white/50 inline-flex items-center gap-1 hover:text-white">
        <ArrowLeft className="w-4 h-4" /> All orders
      </Link>
      <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold font-mono">{o.order_number}</h1>
          <p className="text-white/45 text-sm mt-1">{new Date(o.created_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-sm text-white/55">Set status:</span>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${o.fulfillment_status === s ? "bg-[#7f77dd] text-white" : "bg-[#1d1d2e] text-white/60 hover:text-white"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bls-admin-card p-6">
            <h2 className="font-display font-bold flex items-center gap-2"><Package className="w-4 h-4" /> Items</h2>
            <div className="mt-4 divide-y divide-[#2a2a3d]">
              {o.items.map((it) => (
                <div key={it.product_id} className="py-3 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-md bg-[#16161f] overflow-hidden flex-shrink-0">
                    {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{it.name}</p>
                    <p className="text-xs text-white/50">{money(it.price)} × {it.quantity}</p>
                  </div>
                  <p className="font-mono font-semibold">{money(it.line_total)}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-[#2a2a3d] space-y-1 text-sm">
              <Row k="Subtotal" v={money(o.subtotal)} />
              <Row k="Shipping" v={o.shipping > 0 ? money(o.shipping) : "Free"} />
              {o.tax > 0 && <Row k="Tax" v={money(o.tax)} />}
              <div className="flex justify-between pt-2 border-t border-[#2a2a3d]/50 mt-2">
                <span className="font-display font-bold text-base">Total</span>
                <span className="font-display font-bold text-base">{money(o.total)}</span>
              </div>
            </div>
          </div>

          <div className="bls-admin-card p-6">
            <h2 className="font-display font-bold">Timeline</h2>
            <ol className="mt-4 space-y-3">
              {(o.timeline || []).map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="w-2 h-2 rounded-full bg-[#7f77dd] mt-1.5 flex-shrink-0" />
                  <div>
                    <p>{t.message}</p>
                    <p className="text-xs text-white/40">{new Date(t.at).toLocaleString()}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bls-admin-card p-6">
            <h2 className="font-display font-bold">Customer</h2>
            <div className="mt-3 text-sm space-y-1">
              <p className="text-white">{o.address.full_name}</p>
              <p className="text-white/55">{o.email}</p>
              <p className="text-white/55">{o.address.phone}</p>
            </div>
          </div>

          <div className="bls-admin-card p-6">
            <h2 className="font-display font-bold">Ship to</h2>
            <div className="mt-3 text-sm text-white/75 space-y-1">
              <p>{o.address.line1}</p>
              {o.address.line2 && <p>{o.address.line2}</p>}
              <p>{o.address.city}, {o.address.state} {o.address.postal_code}</p>
              <p>{o.address.country}</p>
            </div>
          </div>

          <div className="bls-admin-card p-6">
            <h2 className="font-display font-bold">Payment</h2>
            <div className="mt-3 text-sm space-y-1">
              <p className="capitalize">{o.payment_method}</p>
              <span className={`bls-status bls-status-${o.payment_status}`}>{o.payment_status}</span>
              {o.payment_status !== "paid" && (
                <button onClick={markPaid} className="bls-admin-btn-ghost mt-3 w-full" data-testid="admin-order-mark-paid">Mark as paid</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-white/55">{k}</span><span>{v}</span></div>;
}
